import React, { useState, useEffect } from "react";
import type { Clip, Track } from "../../types";
import { ZoomIn, ZoomOut, Scissors, Trash2, Undo, Redo, Plus, Type, Smile, Columns, RectangleHorizontal, MousePointer2 } from "lucide-react";
import { TimelineRuler, SmileyPopup, TrackHeader, TrackContent } from './components';
import { TRACK_HEADER_WIDTH, TRACK_HEIGHT_WITH_MARGIN } from './constants';
import { formatDuration } from '../../utils';

interface TimelineAreaProps {
    tracks: Track[];
    setTracks: React.Dispatch<React.SetStateAction<Track[]>>;
    allClips: Clip[];
    onClipsChange: (updater: React.SetStateAction<Clip[]>, saveToHistory?: boolean) => void;
    zoom: number;
    setZoom: React.Dispatch<React.SetStateAction<number>>;
    currentTime: number;
    duration: number;
    selectedClipId: string | null;
    toolMode: 'move' | 'cut';
    setToolMode: (mode: 'move' | 'cut') => void;

    // Refs
    timelineScrollContainerRef: React.RefObject<HTMLDivElement>;
    timelineContentRef: React.RefObject<HTMLDivElement>;
    trackHeadersRef: React.RefObject<HTMLDivElement>;
    tracksContainerRef: React.RefObject<HTMLDivElement>;
    playheadRef: React.RefObject<HTMLDivElement>;

    // Actions
    onSeek: (time: number) => void;
    onNavigateInto?: (clip: Clip) => void;
    onClipSelect: (clipId: string | null, element: HTMLElement | null) => void;
    onDeleteClip: (id: string) => void;
    onAddTrack: (type?: 'overlay' | 'split' | 'media' | 'zoom') => void;
    onDeleteTrack: (id: string) => void;
    onToggleTrackMagnetic: (id: string) => void;
    onToggleTrackVisibility: (id: string) => void;
    onToggleTrackMute: (id: string) => void;
    onTrackVolumeChange: (id: string, volume: number) => void;

    // Complex Actions 
    handleAddTextClip: () => void;
    handleAddSynthe: () => void;
    handleAddPanel: () => void;

    // Toolbar Actions
    undo: () => void;
    redo: () => void;
    historyIndex: number;
    historyLength: number;

    // Interactions
    onClipDragStart: (e: React.MouseEvent, clip: Clip, action: string) => void;
    onTrackDragStart: (e: React.MouseEvent, trackId: string) => void;
    onTimelineBackgroundMouseDown: (e: React.MouseEvent) => void;
    onScrubMouseDown: (e: React.MouseEvent) => void;
    onPlayheadMouseDown: (e: React.MouseEvent) => void;
    onZoomIn: () => void;
    onZoomOut: () => void;
    onZoomToFit: () => void;

    // Smiley
    isSmileyPopupOpen: boolean;
    setIsSmileyPopupOpen: (open: boolean) => void;
    onSmileySelect: (smiley: any) => void;

    // Drop
    dropIndicatorIndex: number | null;
    onFileDrop?: (e: React.DragEvent) => void;

    onTextOverlayDoubleClick?: () => void;

    // Restricted Mode (inside synthe/panel)
    isRestrictedMode?: boolean;
}

export const TimelineArea: React.FC<TimelineAreaProps> = ({
    tracks, setTracks, allClips, onClipsChange, zoom, setZoom, currentTime, duration, selectedClipId, toolMode, setToolMode,
    timelineScrollContainerRef, timelineContentRef, trackHeadersRef, tracksContainerRef, playheadRef,
    onSeek, onNavigateInto, onClipSelect, onDeleteClip, onAddTrack, onDeleteTrack, onToggleTrackMagnetic, onToggleTrackVisibility, onToggleTrackMute, onTrackVolumeChange,
    handleAddTextClip, handleAddSynthe, handleAddPanel,
    undo, redo, historyIndex, historyLength,
    onClipDragStart, onTrackDragStart, onTimelineBackgroundMouseDown, onScrubMouseDown, onPlayheadMouseDown,
    onZoomIn, onZoomOut, onZoomToFit,
    isSmileyPopupOpen, setIsSmileyPopupOpen, onSmileySelect,
    dropIndicatorIndex,
    onFileDrop,
    onTextOverlayDoubleClick,
    isRestrictedMode = false
}) => {
    const [isDragOver, setIsDragOver] = useState(false);

    // Filter tracks in restricted mode (no overlay, split, zoom)
    const RESTRICTED_TRACK_TYPES = ['overlay', 'split', 'zoom'];
    const visibleTracks = isRestrictedMode
        ? tracks.filter(t => !RESTRICTED_TRACK_TYPES.includes(t.type))
        : tracks;

    // Sync scrolling - keeping this enhancement as it fixes usability without breaking behavior
    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        if (trackHeadersRef.current) {
            trackHeadersRef.current.scrollTop = e.currentTarget.scrollTop;
        }
    };

    // Zoom on Wheel / Pinch (Native Listener for non-passive behavior)
    useEffect(() => {
        const container = timelineScrollContainerRef.current;
        if (!container) return;

        const handleWheel = (e: WheelEvent) => {
            // Only zoom on trackpad pinch (ctrlKey) — regular scroll passes through
            if (e.ctrlKey && e.deltaY !== 0 && !isRestrictedMode) {
                e.preventDefault();
                const zoomSensitivity = 2;
                setZoom(prev => {
                    const newZoom = prev - (e.deltaY * zoomSensitivity);
                    return Math.max(0.2, Math.min(500, newZoom));
                });
            }
        };

        // Add with passive: false to allow preventedDefault
        container.addEventListener('wheel', handleWheel, { passive: false });

        return () => {
            container.removeEventListener('wheel', handleWheel);
        };
    }, [setZoom, isRestrictedMode, timelineScrollContainerRef]);

    return (
        <div className="flex-1 flex flex-col min-h-0 bg-[#09090b] relative">
            {/* --- TOOLBAR (Modern Design - Preserved) --- */}
            <div className="h-10 border-b border-white/5 bg-[#09090b] flex items-center px-4 justify-between flex-shrink-0 z-30">
                {/* LEFT: Undo/Redo + Tools */}
                <div className="flex items-center gap-2">
                    <button onClick={undo} disabled={historyIndex <= 0} className="p-1.5 rounded-md hover:bg-white/10 disabled:opacity-30 text-gray-400 hover:text-white transition-colors">
                        <Undo className="w-4 h-4" />
                    </button>
                    <button onClick={redo} disabled={historyIndex >= historyLength - 1} className="p-1.5 rounded-md hover:bg-white/10 disabled:opacity-30 text-gray-400 hover:text-white transition-colors">
                        <Redo className="w-4 h-4" />
                    </button>
                    <div className="w-px h-4 bg-white/10 mx-1" />
                    <div className="flex bg-black/40 rounded-lg p-0.5 border border-white/5">
                        <button
                            onClick={() => setToolMode('move')}
                            className={`p-1.5 rounded-md transition-all ${toolMode === 'move' ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-400 hover:text-white hover:bg-white/5'} `}
                            title="Move Tool (V)"
                        >
                            <MousePointer2 className="w-4 h-4" />
                        </button>
                        <button
                            onClick={() => setToolMode('cut')}
                            className={`p-1.5 rounded-md transition-all ${toolMode === 'cut' ? 'bg-red-600 text-white shadow-sm' : 'text-gray-400 hover:text-white hover:bg-white/5'} `}
                            title="Razor Tool (C)"
                        >
                            <Scissors className="w-4 h-4" />
                        </button>
                    </div>
                    <button
                        onClick={() => selectedClipId && onDeleteClip(selectedClipId)}
                        disabled={!selectedClipId}
                        className="p-1.5 rounded-md hover:bg-red-500/20 text-gray-400 hover:text-red-400 disabled:opacity-30 transition-colors"
                        title="Delete (Del)"
                    >
                        <Trash2 className="w-4 h-4" />
                    </button>
                </div>

                {/* CENTER: Zoom controls */}
                <div className="flex items-center gap-2">
                    <button onClick={onZoomOut} className="p-1.5 rounded-md hover:bg-white/10 text-gray-400 hover:text-white transition-colors">
                        <ZoomOut className="w-4 h-4" />
                    </button>
                    <div className="flex items-center gap-1 bg-black/40 rounded-lg px-2 py-0.5 border border-white/5">
                        <input
                            type="range" min="0.2" max="500" step="0.1" value={zoom}
                            onChange={(e) => setZoom(Number(e.target.value))}
                            className="w-20 h-1 bg-white/10 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-2 [&::-webkit-slider-thumb]:h-2 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-500"
                        />
                        <span className="text-[10px] text-gray-400 w-8 text-right">{Math.round((zoom / 20) * 100)}%</span>
                    </div>
                    <button onClick={onZoomIn} className="p-1.5 rounded-md hover:bg-white/10 text-gray-400 hover:text-white transition-colors">
                        <ZoomIn className="w-4 h-4" />
                    </button>
                    <button onClick={onZoomToFit} className="px-2 py-1 rounded-md bg-white/5 hover:bg-white/10 text-[10px] font-medium text-gray-400 hover:text-white border border-white/5 transition-colors">
                        Fit
                    </button>
                </div>

                {/* RIGHT: Text/Sticker */}
                <div className="flex items-center gap-2">
                    <button onClick={handleAddTextClip} className="flex items-center justify-center w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 border border-white/5 text-gray-300 hover:text-white transition-colors" title="Add Text">
                        <Type className="w-4 h-4 text-blue-400" />
                    </button>
                    <div className="relative">
                        <button
                            onClick={() => setIsSmileyPopupOpen(!isSmileyPopupOpen)}
                            className={`flex items-center justify-center w-8 h-8 rounded-lg border border-white/5 transition-colors ${isSmileyPopupOpen ? 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30' : 'bg-white/5 hover:bg-white/10 text-gray-300 hover:text-white'} `}
                            title="Add Sticker"
                        >
                            <Smile className="w-4 h-4 text-yellow-400" />
                        </button>
                        <SmileyPopup isOpen={isSmileyPopupOpen} onClose={() => setIsSmileyPopupOpen(false)} onSelect={onSmileySelect} />
                    </div>
                </div>
            </div>

            {/* --- TIMELINE STRUCTURE --- */}
            <div className="bg-black flex overflow-hidden flex-1 min-h-0">
                {/* Headers */}
                <div
                    className="relative flex-shrink-0 border-r border-gray-800 flex flex-col"
                    style={{ width: `${TRACK_HEADER_WIDTH}px` }}
                >
                    {/* Fixed Add Track Button */}
                    <div className="h-8 flex items-center justify-center border-b border-gray-800 bg-[#09090b] z-10 flex-shrink-0">
                        <button
                            onClick={() => onAddTrack('media')}
                            className="flex items-center gap-1.5 text-[10px] font-medium text-gray-500 hover:text-white uppercase tracking-wider transition-colors w-full h-full justify-center"
                        >
                            <Plus className="w-3 h-3" />
                            <span>Add Track</span>
                        </button>
                    </div>
                    {/* Scrollable Track Headers */}
                    <div
                        ref={trackHeadersRef}
                        className="flex-1 overflow-y-hidden relative"
                        style={{ overflowY: 'hidden' }}
                    >
                        {/* Drop Indicator */}
                        {dropIndicatorIndex !== null && (
                            <div className="absolute h-0.5 bg-blue-400 w-full z-20 pointer-events-none" style={{ top: `${dropIndicatorIndex * TRACK_HEIGHT_WITH_MARGIN}px`, left: 0 }} />
                        )}
                        {visibleTracks.map((track) => (
                            <TrackHeader
                                key={track.id}
                                track={track}
                                isBeingDragged={false}
                                onToggleMagnetic={() => onToggleTrackMagnetic(track.id)}
                                onToggleVisibility={() => onToggleTrackVisibility(track.id)}
                                onToggleMute={() => onToggleTrackMute(track.id)}
                                onTrackVolumeChange={(volume) => onTrackVolumeChange(track.id, volume)}
                                onDeleteTrack={() => onDeleteTrack(track.id)}
                                onDragStart={(e) => onTrackDragStart(e, track.id)}
                            />
                        ))}
                    </div>
                </div>

                {/* Main Scroll Content */}
                <div
                    ref={timelineScrollContainerRef}
                    className="flex-grow overflow-x-auto overflow-y-auto relative cursor-grab active:cursor-grabbing bg-[#09090b]"
                    onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
                    onDragLeave={() => setIsDragOver(false)}
                    onDrop={(e) => {
                        setIsDragOver(false);
                        if (onFileDrop) onFileDrop(e);
                    }}
                    onMouseDown={onScrubMouseDown}
                    onScroll={handleScroll}
                >
                    {isDragOver && (
                        <div className="absolute inset-0 bg-blue-500/20 border-2 border-dashed border-blue-400 z-50 pointer-events-none flex items-center justify-center">
                            <p className="text-blue-300 font-semibold">Drop media here</p>
                        </div>
                    )}

                    <div
                        ref={timelineContentRef}
                        className="relative min-w-full h-full"
                        style={{ width: `${Math.max(duration * zoom, window.innerWidth - TRACK_HEADER_WIDTH)}px` }}
                        onMouseDown={onTimelineBackgroundMouseDown}
                    >
                        <TimelineRuler zoom={zoom} duration={duration} />

                        <div ref={tracksContainerRef} className="relative h-auto z-0">
                            {/* Playhead */}
                            <div ref={playheadRef} className="absolute top-0 bottom-0 z-30 pointer-events-none" style={{ transform: `translateX(${currentTime * zoom}px)`, height: '100%' }}>
                                <div className="absolute top-0 bottom-0 w-px bg-blue-500 shadow-[0_0_4px_rgba(59,130,246,0.5)]" />
                                <div
                                    className="absolute -top-[22px] left-1/2 -translate-x-1/2 w-4 h-5 bg-blue-500 rounded-sm cursor-ew-resize hover:scale-110 transition-transform shadow-md pointer-events-auto flex items-end justify-center pb-0.5"
                                    style={{ clipPath: 'polygon(0% 0%, 100% 0%, 100% 70%, 50% 100%, 0% 70%)' }}
                                    onMouseDown={onPlayheadMouseDown}
                                >
                                    <div className="w-1.5 h-1.5 rounded-full bg-blue-300/50" />
                                </div>
                            </div>

                            {visibleTracks.map(track => (
                                <TrackContent
                                    key={track.id}
                                    trackId={track.id}
                                    isVisible={track.isVisible}
                                    isBeingDragged={false} // pass prop if available
                                    clips={allClips.filter(c => c.trackId === track.id)}
                                    zoom={zoom}
                                    toolMode={toolMode}
                                    onDeleteClip={onDeleteClip}
                                    onSelectClip={onClipSelect}
                                    selectedClipId={selectedClipId}
                                    onClipDragStart={onClipDragStart}
                                    onZoomTrackMouseDown={undefined} // z1 handling implicit in track?
                                    onDoubleClick={(clip) => {
                                        if ((clip.type === 'synthe' || clip.type === 'panel') && onNavigateInto) {
                                            onNavigateInto(clip);
                                        } else if (clip.type === 'text' && onTextOverlayDoubleClick) {
                                            onTextOverlayDoubleClick();
                                        } else {
                                            onSeek(clip.start);
                                        }
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
